import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';

export default function Setup() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refetch } = useOrg();
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const pending = sessionStorage.getItem('pending_org_name');
    if (pending) setOrgName(pending);
  }, []);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('organisation_members')
      .select('org_id')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          refetch();
          navigate('/dashboard', { replace: true });
        }
      });
  }, [user, navigate, refetch]);

  const handleSetup = async (e) => {
    e.preventDefault();
    if (!orgName.trim()) return;
    setLoading(true);
    setError('');

    const { data: org, error: orgError } = await supabase
      .from('organisations')
      .insert({ name: orgName.trim() })
      .select('id')
      .single();

    if (orgError) {
      setError(`Failed to create organisation: ${orgError.message}`);
      setLoading(false);
      return;
    }

    const { error: memberError } = await supabase
      .from('organisation_members')
      .insert({ org_id: org.id, user_id: user.id, role: 'owner' });

    if (memberError) {
      setError(`Failed to set up membership: ${memberError.message}`);
      setLoading(false);
      return;
    }

    sessionStorage.removeItem('pending_org_name');
    await refetch();
    navigate('/dashboard', { replace: true });
  };

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-orange-500">GroundCheck</h1>
          <p className="text-gray-400 mt-2 text-sm">One more step</p>
        </div>

        <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700">
          <h2 className="text-white font-semibold text-lg mb-2">Name your organisation</h2>
          <p className="text-gray-400 text-sm mb-6">
            You're signed in as <span className="text-white">{user?.email}</span>.
            Create your organisation to continue.
          </p>

          {error && (
            <div className="bg-red-500/20 text-red-400 text-sm rounded-xl px-4 py-3 mb-4 border border-red-500/30">
              {error}
            </div>
          )}

          <form onSubmit={handleSetup} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">
                Organisation Name
              </label>
              <input
                type="text"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                required
                maxLength={100}
                className="w-full bg-gray-700 text-white rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 border border-gray-600"
                placeholder="Acme Drilling Pty Ltd"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-semibold py-3 rounded-xl transition-colors"
            >
              {loading ? 'Setting up…' : 'Continue'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
